// Batch C · Fix List 7 (Q5) — one place for the DD-MMM-YYYY HH:mm
// format used across all Batch-C dialogs. LTR-isolated so Arabic
// contexts don't scramble the date.
// STEP 3 · rule 7 (B-7c) — THE frontend date formatter: no other file defines its own month list or DD-MMM-YYYY
// formatter (src/__tests__/S3R7DatesOneFormatter.test.jsx fails when one comes back). The backend twin is
// backend/engagement_period.py (ddmmmyyyy / fmt_datetime / firm_zone).
export const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
export const MONTHS_AR = ["يناير","فبراير","مارس","أبريل","مايو","يونيو","يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر"];
const TS = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;

/** ISO-8601 (or Date) -> "31-Dec-2024 14:05" in the firm zone (Round 27 · X3). */
export function fmtDateTime(iso) { return fmtDateTimeFirm(iso); }

/** Date-only "31-Dec-2024". A plain date (YYYY-MM-DD) prints as it is; a timestamp (or Date) prints its day in the firm
 *  zone (STEP 3 · B-7c: 23:30 UTC on 28-Feb is 01-Mar in Jeddah — was the UTC day from the ISO prefix). */
export function fmtDate(iso, zone) {
  if (!iso) return "";
  const s = iso instanceof Date ? "" : String(iso);
  if (iso instanceof Date || TS.test(s)) {
    const p = _partsInZone(iso instanceof Date ? iso : new Date(s.replace(/^(\d{4}-\d{2}-\d{2}) /, "$1T")), zone);
    if (p) return `\u2068${p.day}-${p.month}-${p.year}\u2069`;
  }
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `\u2068${m[3]}-${MONTHS[parseInt(m[2],10)-1]}-${m[1]}\u2069`;
  return s;
}

/** Plain "31-Dec-2024" without the LTR marks (for text that is not shown on its own, e.g. a typed field value). */
export function ddmmmyyyy(iso, zone) {
  return fmtDate(iso, zone).replace(/[\u2068\u2069]/g, "");
}

/** "31-Dec-2024" / "31-ديسمبر-2024" for a Date or ISO date (Arabic month names on Arabic screens), no LTR marks. */
export function fmtDateLang(iso, lang) {
  const s = ddmmmyyyy(iso);
  const m = s.match(/^(\d{2})-([A-Za-z]{3})-(\d{4})$/);
  if (!m || lang !== "ar") return s;
  return `${m[1]}-${MONTHS_AR[MONTHS.indexOf(m[2])]}-${m[3]}`;
}

function _partsInZone(d, zone) {
  if (!(d instanceof Date) || isNaN(d.getTime())) return null;
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-GB", { timeZone: zone || firmZone(), day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })
    .formatToParts(d).map((p) => [p.type, p.value]));
  return { day: parts.day, month: MONTHS[["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"].indexOf(String(parts.month).slice(0, 3).toLowerCase())] || parts.month.slice(0, 3),
           year: parts.year, hour: parts.hour === "24" ? "00" : parts.hour, minute: parts.minute };
}

/** 5c · F-5 — "2025-03" → "Mar-2025" (Sub GL Analyzer monthly series). */
export function fmtMonth(ym) {
  if (!ym) return "";
  const m = String(ym).match(/^(\d{4})-(\d{2})/);
  return m ? `\u2068${MONTHS[parseInt(m[2], 10) - 1]}-${m[1]}\u2069` : String(ym);
}

/** Kept for callers; now the firm zone (Round 27 · X3). */
export function fmtDateTimeLocal(iso) { return fmtDateTimeFirm(iso); }

/** Round 27 · X3 — firm time zone (IANA). Set once from the organisation; falls back to the browser zone. */
let _firmZone = null;
export function setFirmZone(z) { _firmZone = z || null; }
export function firmZone() { return _firmZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; }
export function firmZoneAbbr(iso) {
  try {
    const d = iso ? new Date(iso) : new Date();
    return new Intl.DateTimeFormat("en-GB", { timeZone: firmZone(), timeZoneName: "short" }).formatToParts(d).find((p) => p.type === "timeZoneName")?.value || "";
  } catch { return ""; }
}
/** DD-MMM-YYYY HH:mm in the firm zone (LTR-isolated). */
export function fmtDateTimeFirm(iso, zone) {
  if (!iso) return "";
  const p = _partsInZone(iso instanceof Date ? iso : new Date(iso), zone);
  if (!p) return String(iso);
  return `\u2068${p.day}-${p.month}-${p.year} ${p.hour}:${p.minute}\u2069`;
}
/** Founder / Platform-Admin screens: the platform owner's zone (CLAUDE.md: times in KSA) — backend twin FOUNDER_ZONE. */
export const FOUNDER_ZONE = "Asia/Riyadh";

/** DD-MMM-YYYY HH:mm:ss in ``zone`` (or the firm zone), no LTR marks (Founder logs). */
export function fmtDateTimeSec(iso, zone) {
  if (!iso) return "";
  const d = iso instanceof Date ? iso : new Date(iso);
  const p = _partsInZone(d, zone);
  if (!p) return String(iso);
  const ss = new Intl.DateTimeFormat("en-GB", { timeZone: zone || firmZone(), second: "2-digit" }).format(d).padStart(2, "0");
  return `${p.day}-${p.month}-${p.year} ${p.hour}:${p.minute}:${ss}`;
}
/** Round 25f name kept for callers; now the firm zone (owner Round 27: not Jeddah by default). */
export function fmtDateTimeJeddah(iso) { return fmtDateTimeFirm(iso); }
